import { RoleSwitchLoader } from '@components/RoleSwitchLoader';
import { themeColor } from '@config/theme';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { useRoleSwitch } from '@hooks/useRoleSwitch';
import { useWindowSize } from '@hooks/useWindowSize';
import { Backdrop, Box, CircularProgress, CssBaseline, Drawer as MuiDrawer } from '@mui/material';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useLogout } from 'services/auth';
import { useUsersStore } from 'services/users';
import { useGlobalStore } from 'store';
import { Drawer } from './components/Drawer';
import { Header } from './components/Header';
import { Logo, SidebarLogo } from './components/Logo';
import { MobileFooter } from './components/MobileFooter';
import { UserMenu } from './components/UserMenu';
import { backdropStyle, layoutConfig } from './config';
import { useDrawerItems, useMenuItems } from './hooks';
import './styles/index.css';
import { DashboardLayoutProps } from './types';

export const DashboardLayout = (props: DashboardLayoutProps) => {
	const { window } = props;
	const { switchRole, isLoading } = useRoleSwitch();
	const size = useWindowSize();
	const { navigateWithLanguage } = useLanguageNavigate();

	// States
	const [mobileOpen, setMobileOpen] = useState(false);
	const [isLoggingOut, setIsLoggingOut] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	// Custom hooks
	const location = useLocation();
	const { user, setUser, loading } = useUsersStore();
	const { pageTitle } = useGlobalStore();
	const { logout } = useLogout();

	// Derived state
	const open = Boolean(anchorEl);
	const showPrimaryDrawer = size.width > layoutConfig.tabletBreakpoint;
	const isChat = location.pathname.includes('chats');
	const canSwitchRole = Cookies.get('can_switch_role');

	// Event handlers
	const handleDrawerClose = () => setMobileOpen(false);
	const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
	const handleClose = () => setAnchorEl(null);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);

	const handleLogout = async () => {
		handleClose();
		setIsLoggingOut(true);
		await logout();
		setUser(null);
		setIsLoggingOut(false);
		Cookies.remove('can_switch_role');
	};

	// Menu and drawer items
	const { drawerItems, isFarmer } = useDrawerItems({
		userRole: user?.role,
		canSwitchRole,
		userIsActive: user?.is_active,
		onRoleSwitch: switchRole,
	});

	const { menuItems } = useMenuItems({
		userRole: user?.role,
		onLogout: handleLogout,
		isLoggingOut,
		onClose: handleClose,
	});

	const activeItem = drawerItems.find((item) => location.pathname.includes(item.link));
	const container = window !== undefined ? () => window().document.body : undefined;

	if (loading) {
		return <Logo />;
	}

	return (
		<Box
			className={`${!isChat ? 'layout-wrapper' : 'chat-title-wrapper'} bg-[${themeColor.cream}]`}
		>
			<CssBaseline />

			{/* Header */}
			<Header
				user={user}
				size={size}
				isFarmerPath={isFarmer}
				isChat={isChat}
				showPrimaryDrawer={showPrimaryDrawer}
				pageTitle={pageTitle}
				handleDrawerToggle={handleDrawerToggle}
				handleClick={handleClick}
			/>

			{/* Navigation */}
			<Box component="nav" className="layout-wrapper">
				{showPrimaryDrawer ? (
					<MuiDrawer
						variant="persistent"
						sx={{
							'& .MuiDrawer-paper': {
								boxSizing: 'border-box',
								width: { xs: '100px', sm: layoutConfig.drawerWidth },
								backgroundColor: themeColor.darkTeal,
								padding: '20px',
								margin: '20px',
								borderRadius: '12px',
								height: 'calc(100% - 40px)',
								'@media (max-width: 1200px)': {
									width: '100px',
									'& .MuiListItemText-root': {
										display: 'none',
									},
									'& .MuiListItemButton-root:hover .MuiListItemText-root': {
										display: 'block',
										position: 'absolute',
										left: '110px',
										backgroundColor: themeColor.darkTeal,
										padding: '5px 10px',
										borderRadius: '4px',
										boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
									},
								},
							},
						}}
						open={showPrimaryDrawer}
					>
						<Drawer
							logo={
								<SidebarLogo
									onLogoClick={() =>
										navigateWithLanguage(isFarmer ? '/farmer/dashboard' : '/specialist/dashboard')
									}
								/>
							}
							drawerItems={drawerItems}
							navigateWithLanguage={navigateWithLanguage}
							size={size}
							activeItem={activeItem}
						/>
					</MuiDrawer>
				) : (
					<MuiDrawer
						container={container}
						variant="temporary"
						open={mobileOpen}
						onClose={handleDrawerClose}
						ModalProps={{
							keepMounted: true,
						}}
						sx={{
							'& .MuiDrawer-paper': {
								boxSizing: 'border-box',
								width: '100%',
								background: themeColor.darkTeal,
								color: themeColor.contrastTextWhite,
							},
							'& .MuiListItemIcon-root': {
								color: themeColor.contrastTextWhite,
							},
						}}
					>
						<Drawer
							onClose={handleDrawerClose}
							drawerItems={drawerItems}
							navigateWithLanguage={navigateWithLanguage}
							size={size}
							mobileSider
							activeItem={activeItem}
						/>
					</MuiDrawer>
				)}
			</Box>

			{/* Main Content */}
			<Box
				component={`${!isChat ? 'main' : 'div'}`}
				style={{
					minHeight: size.width > layoutConfig.mobileBreakpoint ? 'calc(100vh - 113px)' : 'auto',
					height: size.width > layoutConfig.mobileBreakpoint ? 'calc(100vh - 113px)' : 'auto',
				}}
			>
				<div
					className={classNames('ml-[320px] max-lg:!ml-0 h-full', {
						'!ml-[120px]': size.width <= layoutConfig.largeScreenBreakpoint,
						'pb-[20px]': size.width <= layoutConfig.mobileBreakpoint,
						'mb-[60px]': true,
					})}
				>
					<Outlet />
				</div>

				{/* Mobile Footer */}
				{!mobileOpen && size.width <= layoutConfig.mobileBreakpoint && !isChat && (
					<MobileFooter isFarmerPath={isFarmer} drawerItems={drawerItems} />
				)}
			</Box>

			{/* User Menu */}
			<UserMenu anchorEl={anchorEl} open={open} onClose={handleClose} menuItems={menuItems} />

			{/* Loading Backdrops */}
			<Backdrop sx={backdropStyle} open={isLoggingOut}>
				<CircularProgress color="primary" />
			</Backdrop>

			{/* Role Switching Loader */}
			<RoleSwitchLoader isLoading={isLoading} />
		</Box>
	);
};
